"use client";

import { createContext, useContext, useEffect, useMemo, useRef, ReactNode } from "react";
import { Language, LanguageContextType, SUPPORTED_LANGUAGES } from "@/types/language";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth, useSession, use_lti_mode } from "@/auth/hooks/clerk-hooks";

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const get__user_language = async (): Promise<{ language: string; language_data: Language }> => {
	const response = await fetch("/api/user/language");
	if (!response.ok) throw new Error("Failed to fetch language");
	return response.json();
};

function is_probably_lti_context(): boolean {
	if (typeof window === "undefined") return false;
	const pathname = window.location.pathname;
	const search_params = new URLSearchParams(window.location.search);
	const has_lti_url_marker =
		pathname.includes("/lti/") ||
		pathname.includes("/lti-bootstrap") ||
		search_params.has("lti_message_hint") ||
		search_params.get("lti_embedded") === "true";

	if (has_lti_url_marker) return true;

	try {
		return window.self !== window.top;
	} catch {
		return true;
	}
}

export function LanguageProvider({ children, onLanguageChange }: { children: ReactNode; onLanguageChange?: (code: string) => void }) {
	const query_client = useQueryClient();
	const { isSignedIn, isLoaded, sessionClaims } = useAuth();
	const { session } = useSession();
	const is_lti = use_lti_mode();
	const session_reload_attempts = useRef(0);

	useEffect(() => {
		if (is_lti || is_probably_lti_context() || !isLoaded || isSignedIn !== true || sessionClaims?.userId || !session) return;
		if (session_reload_attempts.current >= 3) return;

		let cancelled = false;
		const reload_session = () => {
			if (cancelled || session_reload_attempts.current >= 3) return;
			session_reload_attempts.current += 1;
			session.reload().catch((error) => {
				console.warn("[LanguageProvider] Session reload failed while waiting for userId claim", error);
			});
		};

		reload_session();
		const interval = window.setInterval(reload_session, 1000);

		return () => {
			cancelled = true;
			window.clearInterval(interval);
		};
	}, [is_lti, isLoaded, isSignedIn, sessionClaims?.userId, session]);

	useEffect(() => {
		if (sessionClaims?.userId) session_reload_attempts.current = 0;
	}, [sessionClaims?.userId]);

	const { data: user_language_data, isLoading } = useQuery({
		queryKey: ["user-language"],
		queryFn: get__user_language,
		staleTime: 1000 * 60 * 5,
		enabled: isLoaded && isSignedIn === true && Boolean(sessionClaims?.userId),
	});

	const current_language = useMemo(() => {
		const code = user_language_data?.language || "en-gb";
		return SUPPORTED_LANGUAGES.find((lang) => lang.code === code) || SUPPORTED_LANGUAGES[0];
	}, [user_language_data]);

	const set__language = (language: Language) => {
		query_client.setQueryData(["user-language"], {
			language: language.code,
			language_data: language
		});
		if (onLanguageChange) onLanguageChange(language.code);
	};

	return (
		<LanguageContext.Provider value={{ current_language, set__language, isLoading }}>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
}
